import React, { useState, useEffect } from 'react';
import {
  useGetBlogListQuery,
  useGetBlogCategoriesQuery,
} from '../redux/apiSlice';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertTriangle,
  BookOpen,
  Calendar,
  Tag,
  User,
} from 'lucide-react';

interface Blog {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  alttext: string;
  featuredImage: string;
  delete: boolean;
  slug: string;
  tags: string[];
  publishedAt: string;
  status: number;
  seoTitle: string;
  seoDescription: string;
  author: {
    id: number;
    name: string;
  };
  categories: Array<{
    id: number;
    name: string;
    slug: string;
    postCount: number;
  }>;
}

interface Category {
  id: number;
  name: string;
  slug: string;
  postCount: number;
}

const BlogList: React.FC = () => {
  const { pageNumber } = useParams<{ pageNumber?: string }>();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(
    pageNumber ? parseInt(pageNumber, 10) : 1,
  );
  const pageSize = 9;
  const { data, isLoading, error } = useGetBlogListQuery({
    page: currentPage,
    pageSize,
  });
  const { data: categoriesData, isLoading: categoriesLoading } =
    useGetBlogCategoriesQuery();

  // Update current page when URL parameter changes
  useEffect(() => {
    if (pageNumber) {
      const page = parseInt(pageNumber, 10);
      if (page !== currentPage && page > 0) {
        setCurrentPage(page);
      }
    }
  }, [pageNumber, currentPage]);

  // Use real categories from API or fallback to static ones
  const categories: Category[] = categoriesData || [
    { id: 1, name: 'Programming', slug: 'programming', postCount: 12 },
    { id: 2, name: 'Web Development', slug: 'web-development', postCount: 8 },
    { id: 3, name: 'Mobile Apps', slug: 'mobile-apps', postCount: 5 },
    { id: 4, name: 'AI & Machine Learning', slug: 'ai-ml', postCount: 7 },
    { id: 5, name: 'Game Development', slug: 'game-dev', postCount: 4 },
    { id: 6, name: 'Tips & Tricks', slug: 'tips-tricks', postCount: 15 },
  ];

  // Function to get default image if featuredImage is not available
  const getImageSrc = (featuredImage: string) => {
    if (!featuredImage || featuredImage === '') {
      return '/assets/images/final_logo.png'; // Default image
    }
    // Check if it's a full URL or relative path
    if (featuredImage.startsWith('http')) {
      return featuredImage;
    }
    // Assume it's from the API server
    return `${process.env.REACT_APP_API_URL}${featuredImage}`;
  };

  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Function to get category names - categories are already objects in the blog data
  const getCategoryNames = (blogCategories: any[]) => {
    // If categories are already objects with name property, return them directly
    if (
      blogCategories &&
      blogCategories.length > 0 &&
      typeof blogCategories[0] === 'object'
    ) {
      return blogCategories.map((cat) => cat.name);
    }
    // Fallback: if categories are IDs, map them to names
    return categories
      .filter((cat) => blogCategories.includes(cat.id))
      .map((cat) => cat.name);
  };

  const handlePageChange = (page: number) => {
    if (page === 1) {
      navigate('/blog');
    } else {
      navigate(`/blog/page/${page}`);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPaginationPages = () => {
    if (!data || data.length === 0) return [];

    const totalPages = Math.ceil(data.length / pageSize);
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin text-[#004aad] mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-600">
            Loading amazing blogs...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-lg p-8 max-w-md">
          <div className="text-red-500 mb-4">
            <AlertTriangle className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            Oops! Something went wrong
          </h3>
          <p className="text-gray-600 mb-4">
            We couldn't load the blog posts right now.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#004aad] hover:bg-blue-800 text-white px-6 py-2 rounded-full font-medium transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const blogs = data || [];
  const totalPages = blogs.length > 0 ? Math.ceil(blogs.length / pageSize) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50">
      {/* Header Section */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Coding Resource Blog
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Discover fun blogs, coding tips, and exciting adventures in the
              world of programming!
            </p>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Blog Content */}
          <div className="flex-1">
            {blogs.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-gray-400 mb-4">
                  <BookOpen className="w-24 h-24 mx-auto" />
                </div>
                <h3 className="text-2xl font-bold text-gray-500 mb-2">
                  No Blogs Yet!
                </h3>
                <p className="text-gray-400">
                  Check back soon for exciting coding adventures!
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                {blogs.map((blog: Blog) => (
                  <Link
                    key={blog.id}
                    to={`/blog/${blog.slug}`}
                    className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    {/* Blog Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={getImageSrc(blog.featuredImage)}
                        alt={blog.alttext || blog.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/assets/images/final_logo.png';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>

                    {/* Blog Content */}
                    <div className="p-6">
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(blog.publishedAt)}
                        </div>
                        <div className="flex items-center">
                          <User className="w-4 h-4 mr-1" />
                          {blog.author.name}
                        </div>
                      </div>

                      <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#004aad] transition-colors duration-200 line-clamp-2">
                        {blog.title}
                      </h2>

                      <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-4">
                        {blog.excerpt}
                      </p>

                      {/* Categories */}
                      {blog.categories.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {getCategoryNames(blog.categories)
                            .slice(0, 2)
                            .map((category, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-[#004aad]"
                              >
                                <Tag className="w-3 h-3 mr-1" />
                                {category}
                              </span>
                            ))}
                          {getCategoryNames(blog.categories).length > 2 && (
                            <span className="text-xs text-gray-500">
                              +{getCategoryNames(blog.categories).length - 2}{' '}
                              more
                            </span>
                          )}
                        </div>
                      )}

                      {/* Read More Button */}
                      <div className="flex items-center text-[#004aad] font-medium text-sm group-hover:text-blue-800">
                        <span>Read More</span>
                        <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-200" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-12">
                {/* Previous Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`flex items-center px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                    currentPage === 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-[#004aad] hover:bg-blue-100 hover:text-blue-800'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex space-x-1">
                  {getPaginationPages().map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-10 h-10 rounded-full font-medium transition-all duration-200 ${
                        currentPage === page
                          ? 'bg-[#004aad] text-white shadow-lg'
                          : 'text-gray-600 hover:bg-blue-100 hover:text-blue-800'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`flex items-center px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                    currentPage === totalPages
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-[#004aad] hover:bg-blue-100 hover:text-blue-800'
                  }`}
                >
                  Next
                  <ChevronRight className="w-5 h-5 ml-1" />
                </button>
              </div>
            )}

            {/* Page Info */}
            {blogs.length > 0 && (
              <div className="text-center mt-6 text-gray-500 text-sm">
                Showing {blogs.length} amazing blogs
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:w-80">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                <span className="bg-gradient-to-r from-[#004aad] to-blue-600 bg-clip-text text-transparent">
                  Categories
                </span>
              </h3>

              <div className="space-y-3">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/blog/category/${category.slug}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-blue-50 transition-colors duration-200 group"
                  >
                    <div className="flex items-center">
                      <Tag className="w-4 h-4 text-[#004aad] mr-3" />
                      <span className="text-gray-700 group-hover:text-[#004aad] font-medium">
                        {category.name}
                      </span>
                    </div>
                    <span className="bg-blue-100 text-[#004aad] text-sm px-2 py-1 rounded-full font-medium">
                      {category.postCount}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogList;
